/**
 * Agent Hub API client — all backend calls go through here.
 *
 * This is the hub-adapted version of the POC's agentHub.ts.
 * Same function signatures, but calls the hub's backend endpoints:
 *   - /auth/test-token  (dev mode persona tokens)
 *   - /auth/me          (user identity)
 *   - /api/v1/agents    (agent catalog)
 *   - /api/v1/chat      (SSE chat stream)
 *
 * In dev mode the Vite proxy rewrites /api → http://localhost:8000
 * and /auth → http://localhost:8000.
 */

/** Default timeout (ms) for non-streaming API requests. */
import { handleSessionExpired } from '@/shared/auth/logout';

const REQUEST_TIMEOUT_MS = 30_000;

/** Default timeout (ms) for SSE streaming requests (agent chat). */
const DEFAULT_STREAM_TIMEOUT_MS = 3_600_000;
let streamTimeoutMs = DEFAULT_STREAM_TIMEOUT_MS;
/** Debounce for streaming content updates pushed to background store. */
const CONTENT_UPDATE_DEBOUNCE_MS = 75;

function setStreamTimeoutMs(timeoutMs: number): void {
  if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
    streamTimeoutMs = timeoutMs;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  externalSignal?: AbortSignal,
): Promise<T> {
  // No Authorization header — session cookie is sent automatically via credentials: 'include'.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  // Abort after timeout to prevent indefinite hangs. If the caller passed an
  // external signal, abort early when it fires (e.g. effect cleanup).
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
  }

  try {
    const resp = await fetch(path, {
      ...options,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    // 401 means the backend session cookie is dead (the backend already tried refresh).
    if (resp.status === 401) {
      handleSessionExpired();
      throw new Error('Session expired');
    }

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ detail: resp.statusText }));
      throw new Error(body.detail ?? `${resp.status} ${resp.statusText}`);
    }
    const text = (await resp.text()).trim();
    return (text ? JSON.parse(text) : undefined) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (externalSignal?.aborted) {
        throw err;
      }
      throw new Error(`Request to ${path} timed out after ${REQUEST_TIMEOUT_MS / 1000}s`, { cause: err });
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
  }
}

// --- Auth ---

export interface TokenResponse {
  token: string;
  user: {
    user_id: string;
    email: string;
    name: string;
    groups: string[];
  };
  allowed_agents: string[];
  is_admin: boolean;
}

/**
 * Fetch a dev test token using a persona name (e.g. "admin", "esg_user").
 *
 * The backend expects ?persona=<name> where name is one of:
 *   admin, esg_user, spog_user, retention_user, viewer
 */
export async function fetchTestToken(persona: string): Promise<TokenResponse> {
  return request(`/auth/test-token?persona=${encodeURIComponent(persona)}`);
}

export async function fetchMe(): Promise<Record<string, unknown>> {
  return request('/auth/me');
}

// --- IAM Access Requests ---

export async function requestIamAccess(agentId: string, ldapIds?: string): Promise<void> {
  await request('/api/v1/iam/request-access', {
    method: 'POST',
    body: JSON.stringify({ agent_id: agentId, ...(ldapIds && { ldap_ids: ldapIds }) }),
  });
}

// --- Agent Catalog ---

export interface AgentInfo {
  agent_id: string;
  display_name: string;
  description: string;
  version: string;
  runtime: string;
  tags: string[];
  mcps: string[];
  account_type?: 'single' | 'cross' | 'solution' | 'orchestrator' | null;
  dev_status?: 'live' | 'experimental' | 'development' | 'planned' | null;
  bullets?: string[];
  example_prompts?: string[];
}

export interface DeniedAgentInfo {
  agent_id: string;
  display_name: string;
  description: string;
  account_type?: 'single' | 'cross' | 'solution' | 'orchestrator' | null;
  dev_status?: 'live' | 'experimental' | 'development' | 'planned' | null;
  bullets?: string[];
  required_groups: string[];
}

export interface AgentCatalog {
  agents: AgentInfo[];
  denied_agents: DeniedAgentInfo[];
  is_admin: boolean;
}

/**
 * Fetch the agent catalog from the backend.
 * Returns the list of agents the authenticated user can access.
 */
export async function fetchAgentCatalog(): Promise<AgentCatalog> {
  const resp = await request<AgentCatalog>('/api/v1/agents');
  return {
    agents: resp.agents || [],
    denied_agents: resp.denied_agents || [],
    is_admin: resp.is_admin ?? false,
  };
}

// --- Capabilities ---

export interface SkillInfo {
  name: string;
  description: string;
  allowed_groups: string[];
  required_tools: string[];
  required_mcps: string[];
  required_datasets: string[];
  example_prompts?: string[];
}

export interface MCPServerInfo {
  name: string;
  description: string;
  transport: string;
  allowed_groups: string[];
}

export interface DatasetInfo {
  name: string;
  description: string;
  path: string;
  allowed_groups: string[];
}

export interface DeniedCapability {
  name: string;
  type: string;
  description: string;
  required_groups: string[];
}

export interface TimeoutSettings {
  ui_stream_timeout_seconds: number;
  backend_stream_timeout_seconds: number;
  llm_idle_timeout_seconds: number;
}

export interface Capabilities {
  /** External Okta ``sub``. Stable, but NOT the internal users.id UUID. */
  user_id: string;
  /** Internal users.id resolved from user_id. Null until the user row exists
   *  in the DB. Use this (never user_id) when comparing against owner rows. */
  internal_user_id: string | null;
  email: string;
  display_name?: string;
  groups: string[];
  auth_method: string;
  is_admin: boolean;
  /** True when the hub considers the caller a real platform admin (distinct from group-admin). */
  is_real_admin: boolean;
  /** Agents the caller can CHAT with (group-based access). */
  allowed_agents: string[];
  /** Agents the caller explicitly owns (primary or co-owner rows in DB).
   *  Disjoint from allowed_agents — use this to gate the settings UI so it
   *  matches what the backend owner APIs will actually authorize. */
  owned_agents: string[];
  skills: SkillInfo[];
  mcp_servers: MCPServerInfo[];
  datasets: DatasetInfo[];
  denied: DeniedCapability[];
  timeouts?: TimeoutSettings;
  /** IAM group names with a pending access request (from /auth/me). */
  pending_iam_groups?: string[];
}

/**
 * Fetch capabilities from the hub backend.
 *
 * Fires /auth/me and /api/v1/agents in parallel, then assembles a Capabilities object.
 * Returns both capabilities and the agent catalog to avoid duplicate fetches.
 */
export async function fetchCapabilities(): Promise<{
  capabilities: Capabilities;
  agents: AgentInfo[];
  deniedAgents: DeniedAgentInfo[];
}> {
  const [me, catalogResp] = await Promise.all([
    fetchMe(),
    request<{ agents: AgentInfo[]; denied_agents?: DeniedAgentInfo[] }>('/api/v1/agents').catch(
      () => ({ agents: [] as AgentInfo[], denied_agents: [] as DeniedAgentInfo[] }),
    ),
  ]);

  const agents = catalogResp.agents || [];
  const deniedAgents = catalogResp.denied_agents || [];

  const userId = (me.user_id as string) || 'unknown';
  const internalUserId = (me.internal_user_id as string | null | undefined) ?? null;
  const email = (me.email as string) || '';
  const displayName = (me.display_name as string) || (me.name as string) || undefined;
  const groups = (me.groups as string[]) || [];
  const isAdmin = (me.is_admin as boolean) || false;
  const isRealAdmin = (me.is_real_admin as boolean) ?? isAdmin;
  const allowedAgents = (me.allowed_agents as string[] | undefined) ?? [];
  const ownedAgents = (me.owned_agents as string[] | undefined) ?? [];
  const pendingIamGroups = (me.pending_iam_groups as string[] | undefined) ?? [];
  const authMethod = import.meta.env.DEV ? 'dev_token' : 'okta';

  const meTimeouts = me.timeouts as Partial<TimeoutSettings> | undefined;
  const resolvedUiTimeoutSeconds = Number(meTimeouts?.ui_stream_timeout_seconds);
  if (Number.isFinite(resolvedUiTimeoutSeconds) && resolvedUiTimeoutSeconds > 0) {
    setStreamTimeoutMs(resolvedUiTimeoutSeconds * 1000);
  } else {
    setStreamTimeoutMs(DEFAULT_STREAM_TIMEOUT_MS);
  }
  const timeouts: TimeoutSettings = {
    ui_stream_timeout_seconds: Number.isFinite(resolvedUiTimeoutSeconds) && resolvedUiTimeoutSeconds > 0
      ? resolvedUiTimeoutSeconds
      : Math.floor(DEFAULT_STREAM_TIMEOUT_MS / 1000),
    backend_stream_timeout_seconds: Number(meTimeouts?.backend_stream_timeout_seconds) || 3600,
    llm_idle_timeout_seconds: Number(meTimeouts?.llm_idle_timeout_seconds) || 3600,
  };

  const skills: SkillInfo[] = [];

  const mcpMap = new Map<string, MCPServerInfo>();
  for (const a of agents) {
    for (const mcpId of (a.mcps || [])) {
      if (!mcpMap.has(mcpId)) {
        mcpMap.set(mcpId, {
          name: mcpId,
          description: `MCP server: ${mcpId}`,
          transport: 'http',
          allowed_groups: ['*'],
        });
      }
    }
  }

  return {
    capabilities: {
      user_id: userId,
      internal_user_id: internalUserId,
      email,
      display_name: displayName,
      groups,
      auth_method: authMethod,
      is_admin: isAdmin,
      is_real_admin: isRealAdmin,
      allowed_agents: allowedAgents,
      owned_agents: ownedAgents,
      skills,
      mcp_servers: Array.from(mcpMap.values()),
      datasets: [],
      denied: [],
      timeouts,
      pending_iam_groups: pendingIamGroups,
    },
    agents,
    deniedAgents,
  };
}


// --- Agent Run ---

export interface AgentRunResponse {
  response: string;
  tool_calls: { tool: string; args: Record<string, unknown>; result?: unknown; turn_number?: number }[];
  thinking_stream?: string;
  turns_used: number;
  skill_used: string | null;
  datasets_available: string[];
  tools_available: string[];
  execution_time_ms: number;
  /** Run ID for feedback (from X-Run-ID header) */
  run_id?: string;
  /** Thread ID (from X-Conversation-ID header) */
  thread_id?: string;
  /** Request ID (from X-Request-ID header) */
  request_id?: string;
  /** Assistant message ID (from final done event payload) */
  message_id?: string;
  /** Terminal run status persisted by backend */
  run_status?: RunStatus;
  /** Persisted backend error message for failed/cancelled runs */
  run_error_message?: string | null;
  /** Backend-generated download links for files produced in outputs/. */
  artifacts?: ArtifactLink[];
}

export interface ArtifactLink {
  artifact_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  download_url: string;
  expires_at?: string;
}


export interface ChatRequestMetadata {
  [key: string]: unknown;
}

/**
 * Run an agent via the hub's SSE chat endpoint.
 *
 * The hub uses POST /api/v1/chat with SSE streaming.
 * We collect the streamed events and return a response compatible
 * with the POC's AgentRunResponse interface.
 */
export type RunStatus = 'running' | 'cancelling' | 'completed' | 'failed' | 'cancelled' | 'timed_out';

export interface CancelResponse {
  status: 'cancelling' | 'not_found' | `already_${RunStatus}`;
  request_id?: string;
  thread_id?: string;
  run_id?: string;
}

export interface ActiveRunInfo {
  request_id?: string;
  thread_id?: string;
  run_id?: string;
  agent_id?: string;
  status?: RunStatus;
}

export class ChatConflictError extends Error {
  activeRun?: ActiveRunInfo;

  constructor(message: string, activeRun?: ActiveRunInfo) {
    super(message);
    this.name = 'ChatConflictError';
    this.activeRun = activeRun;
  }
}

function makeRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Progress updates during agent streaming.
 * Status events (eventType === 'status') provide interactive progress messages
 * (e.g. progress messages from tools or retrieval) surfaced
 * in the transient status bar via onProgress.
 */
export interface StreamProgress {
  status: string;
  detail?: string;
  toolName?: string;
  /**
   * Cumulative thinking string. Desktop drives live thinking via the explicit
   * onThinkingDelta / onThinkingReplace callbacks on runAgent / resubscribeToStream;
   * this field is retained for the mobile chat surface, which still consumes it.
   */
  thinking?: string;
}

/** Callback for streaming content updates — used for background streaming when user navigates away. */
export type OnContentUpdate = (
  content: string,
  isComplete: boolean,
  threadId?: string,
  ids?: { runId?: string; messageId?: string },
) => void;

export async function cancelRequestById(requestId: string): Promise<CancelResponse> {
  return request<CancelResponse>(`/api/v1/chat/cancel/${encodeURIComponent(requestId)}`, {
    method: 'POST',
  });
}

export async function cancelRequestByThread(threadId: string): Promise<CancelResponse> {
  return request<CancelResponse>(
    `/api/v1/chat/cancel/thread/${encodeURIComponent(threadId)}`,
    { method: 'POST' },
  );
}

/**
 * Poll until the backend run for `threadId` reaches a terminal state
 * (completed / failed / cancelled / timed_out).
 * Returns the ThreadDetail once settled, or null if aborted / max-attempts exceeded.
 */
export async function waitForTerminalState(
  threadId: string,
  signal?: AbortSignal,
  maxAttempts = 10,
  intervalMs = 500,
): Promise<ThreadDetail | null> {
  const TERMINAL: Set<RunStatus> = new Set(['completed', 'failed', 'cancelled', 'timed_out']);
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise<void>((r) => setTimeout(r, intervalMs));
    if (signal?.aborted) return null;
    try {
      const detail = await fetchThreadMessages(threadId, signal);
      const lastRun = [...detail.messages].reverse().find((m) => m.role === 'assistant');
      if (lastRun?.run_status && TERMINAL.has(lastRun.run_status)) return detail;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Parsed SSE event surfaced by `parseSseStream` — wire-format only, no app semantics.
 *
 * `type` is the resolved event-name (from the SSE `event:` line, or as a
 * fallback the payload's `type` / `event` field). Empty string when the event
 * couldn't be classified — consumers should treat unknown types defensively.
 *
 * `raw` is the full parsed JSON payload from the `data:` line. Consumers reach
 * into `raw.data?.foo ?? raw.foo` themselves; field names live with the
 * consumer because they vary per event type and per backend version.
 */
interface ParsedSseEvent {
  type: string;
  raw: Record<string, unknown> & { data?: Record<string, unknown>; type?: string; event?: string };
}

/**
 * Shared SSE wire-format parser. Reads from `reader`, decodes UTF-8, splits on
 * lines, tracks the current `event:` type, and yields one `ParsedSseEvent` per
 * `data:` line. Skips heartbeats (`[DONE]` literal, malformed JSON).
 *
 * Used by `runAgent` (originating POST stream) and `resubscribeToStream`
 * (late-attach GET stream) so the wire format lives in one place. Both
 * endpoints emit the same SSE shape; only the per-event semantics (what to do
 * with a `text_delta` etc.) differ — those stay in the call sites.
 *
 * `options.onChunk` fires after each successful `reader.read()` so callers
 * that need to reset an idle-timeout can do so without re-implementing the
 * read loop.
 *
 * The reader's lock is released in the generator's `finally` so the response
 * body can be GC'd promptly even if the consumer breaks out of `for await`.
 */
async function* parseSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options?: { onChunk?: () => void },
): AsyncGenerator<ParsedSseEvent> {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      options?.onChunk?.();

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.slice(7).trim();
          continue;
        }
        if (!line.startsWith('data: ')) continue;
        const dataStr = line.slice(6).trim();
        if (!dataStr || dataStr === '[DONE]') {
          currentEventType = '';
          continue;
        }

        let parsed: ParsedSseEvent['raw'];
        try {
          parsed = JSON.parse(dataStr);
        } catch {
          // Malformed JSON — skip the line but reset event-type so the next
          // valid data line isn't tagged with a stale event name.
          currentEventType = '';
          continue;
        }

        const eventType = currentEventType || parsed.type || parsed.event || '';
        currentEventType = '';
        yield { type: eventType, raw: parsed };
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Already released or stream errored — nothing to do.
    }
  }
}

interface ChatSseHandlers {
  onTextDelta?: (text: string) => void;
  onThinking?: (text: string) => void;
  onThinkingDelta?: (text: string) => void;
  onToolCallStart?: (toolName: string, args: Record<string, unknown>) => void;
  onToolCallEnd?: (result: string) => void;
  onStatus?: (message: string) => void;
  onError?: (message: string) => void;
  onDone?: (data: Record<string, unknown>) => void;
  onUnknownText?: (text: string) => void;
}

// Never forward the backend's raw error text into the transcript - it can carry
// internal/network details. Always show curated, user-safe copy here.
function normalizeStreamErrorMessage(): string {
  return 'The agent encountered an error. Please try again.';
}

async function consumeChatSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: ChatSseHandlers,
  options?: { onChunk?: () => void },
): Promise<void> {
  for await (const { type, raw } of parseSseStream(reader, options)) {
    const data = raw.data ?? {};
    if (type === 'text_delta') {
      const text = (data.text ?? raw.text ?? '') as string;
      if (typeof text === 'string' && text.length > 0) {
        handlers.onTextDelta?.(text);
      }
    } else if (type === 'thinking_delta') {
      const thought = (data.text || data.content || '') as string;
      if (thought) handlers.onThinkingDelta?.(thought);
    } else if (type === 'thinking') {
      const thought = (data.text || data.content || '') as string;
      handlers.onThinking?.(thought);
    } else if (type === 'error') {
      handlers.onError?.(normalizeStreamErrorMessage());
    } else if (type === 'tool_call_start') {
      const toolName = (data.tool_name as string) || 'unknown';
      handlers.onToolCallStart?.(toolName, (data.args as Record<string, unknown>) || {});
    } else if (type === 'tool_call_end') {
      handlers.onToolCallEnd?.((data.result as string) || '');
    } else if (type === 'status') {
      const message = (data.message ?? data.text ?? '') as string;
      if (message) handlers.onStatus?.(message);
    } else if (type === 'done') {
      handlers.onDone?.(data);
    } else {
      const text = (data.text || raw.text || '') as string;
      if (text) handlers.onUnknownText?.(text);
    }
  }
}

export interface RunAgentOptions {
  /** Caller-owned AbortController signal (SDK pattern). Abort to stop the stream. */
  signal: AbortSignal;
  /** Client-generated request ID (sent as X-Request-ID header). */
  requestId?: string;
  /** Existing thread ID for multi-turn continuity. Omit for new threads. */
  threadId?: string;
  onProgress?: (progress: StreamProgress) => void;
  onThreadCreated?: (threadId: string) => void;
  onThinkingDelta?: (delta: string) => void;
  onThinkingReplace?: (thinking: string) => void;
  onContentUpdate?: OnContentUpdate;
  metadata?: ChatRequestMetadata;
}

export async function runAgent(
  prompt: string,
  agentId: string,
  options: RunAgentOptions,
): Promise<AgentRunResponse> {
  const {
    signal,
    onProgress,
    onThreadCreated,
    onContentUpdate,
    onThinkingDelta,
    onThinkingReplace,
    metadata,
  } = options;
  // No Authorization header — session cookie sent automatically via credentials: 'include'.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (!agentId) throw new Error('agent_id is required');

  const startTime = Date.now();
  const conversationId = options.threadId ?? undefined;
  const requestId = options.requestId ?? makeRequestId();

  const chatPayload: Record<string, unknown> = {
    agent_id: agentId,
    prompt,
    conversation_id: conversationId,
  };
  const chatMetadata: ChatRequestMetadata = metadata ? { ...metadata } : {};
  if (Object.keys(chatMetadata).length > 0) {
    chatPayload.metadata = chatMetadata;
  }

  headers['X-Request-ID'] = requestId;

  // Relay caller's abort signal + internal timeout into a single controller.
  const internalController = new AbortController();
  const relayAbort = () => internalController.abort();
  if (signal.aborted) internalController.abort();
  else signal.addEventListener('abort', relayAbort, { once: true });

  let timeoutId = setTimeout(relayAbort, streamTimeoutMs);
  const resetStreamTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(relayAbort, streamTimeoutMs);
  };
  const cleanupRequest = () => {
    clearTimeout(timeoutId);
    signal.removeEventListener('abort', relayAbort);
  };

  let resp: Response;
  try {
    resp = await fetch('/api/v1/chat', {
      method: 'POST',
      headers,
      credentials: 'include',
      signal: internalController.signal,
      body: JSON.stringify(chatPayload),
    });

    // 401 means the backend session is dead (backend already tried refresh).
    if (resp.status === 401) {
      handleSessionExpired();
      throw new Error('Session expired');
    }
  } catch (err) {
    cleanupRequest();
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (signal.aborted) throw new Error('Request cancelled by user', { cause: err });
      throw new Error(`Agent request timed out after ${Math.floor(streamTimeoutMs / 1000)}s`, { cause: err });
    }
    const msg = err instanceof Error ? err.message : String(err);
    if (msg === 'Failed to fetch' || (err instanceof TypeError && msg.includes('fetch'))) {
      throw new Error(
        'Failed to connect to the server. Ensure the backend is running on port 8000 (try docker compose up --build).',
        { cause: err },
      );
    }
    throw err;
  }

  if (!resp.ok) {
    cleanupRequest();
    const body = await resp.json().catch(() => ({ detail: resp.statusText }));
    if (resp.status === 409 && body.active_run) {
      throw new ChatConflictError(
        typeof body.detail === 'string' ? body.detail : 'A run is already in progress for this thread',
        body.active_run as ActiveRunInfo,
      );
    }
    throw new Error(typeof body.detail === 'string' ? body.detail : `${resp.status} ${resp.statusText}`);
  }

  const threadId = resp.headers.get('X-Conversation-ID');
  const effectiveThreadId = threadId || conversationId || `thread-${agentId}-${Date.now()}`;
  if (threadId) onThreadCreated?.(threadId);
  const runId = resp.headers.get('X-Run-ID');
  const responseRequestId = resp.headers.get('X-Request-ID') || requestId;

  const reader = resp.body?.getReader();
  if (!reader) {
    cleanupRequest();
    throw new Error('No response body');
  }

  let responseText = '';
  const toolCalls: AgentRunResponse['tool_calls'] = [];
  let thinking_stream = '';
  let turnsUsed = 0;
  const toolsAvailable: string[] = [];
  let messageId: string | undefined;
  let runStatus: RunStatus | undefined;
  let artifacts: ArtifactLink[] | undefined;
  let contentUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  let receivedDoneEvent = false;

  const emitContentUpdate = (isComplete: boolean) => {
    if (!onContentUpdate) return;
    const ids = { runId: runId ?? undefined, messageId };
    if (isComplete) {
      if (contentUpdateTimer) {
        clearTimeout(contentUpdateTimer);
        contentUpdateTimer = null;
      }
      onContentUpdate(responseText, true, effectiveThreadId, ids);
      return;
    }
    if (contentUpdateTimer) return;
    contentUpdateTimer = setTimeout(() => {
      contentUpdateTimer = null;
      onContentUpdate(responseText, false, effectiveThreadId, ids);
    }, CONTENT_UPDATE_DEBOUNCE_MS);
  };

  const emitProgress = (progress: StreamProgress) => {
    onProgress?.(progress);
  };

  const replaceThinking = (thought: string) => {
    if (!thought) return;
    thinking_stream = thought;
    onThinkingReplace?.(thinking_stream);
    emitProgress({ status: 'Thinking...', thinking: thinking_stream });
  };

  const appendThinkingDelta = (delta: string) => {
    if (!delta) return;
    thinking_stream += delta;
    onThinkingDelta?.(delta);
    emitProgress({ status: 'Thinking...', thinking: thinking_stream });
  };

  try {
    await consumeChatSseStream(reader, {
      onTextDelta: (text) => {
        responseText += text;
        emitProgress({ status: 'Generating response...' });
        emitContentUpdate(false);
      },
      onThinking: (thought) => {
        if (thought) {
          replaceThinking(thought);
        } else {
          emitProgress({ status: 'Thinking...' });
        }
      },
      onThinkingDelta: (delta) => {
        appendThinkingDelta(delta);
      },
      onError: (errorMsg) => {
        responseText += `[Agent Error] ${errorMsg}`;
        emitProgress({ status: 'Error occurred' });
        emitContentUpdate(true);
      },
      onToolCallStart: (toolName, args) => {
        toolCalls.push({
          tool: toolName,
          args,
          turn_number: turnsUsed,
        });
        const friendlyName = toolName.replace(/__/g, ' → ').replace(/_/g, ' ');
        emitProgress({ status: `Calling tool: ${friendlyName}`, toolName });
      },
      onToolCallEnd: (result) => {
        const lastTc = toolCalls[toolCalls.length - 1];
        if (lastTc) {
          lastTc.result = result;
          const friendlyName = lastTc.tool.replace(/__/g, ' → ').replace(/_/g, ' ');
          emitProgress({ status: `Got results from ${friendlyName}`, toolName: lastTc.tool });
        }
      },
      onStatus: (message) => emitProgress({ status: message }),
      onDone: (data) => {
        receivedDoneEvent = true;
        if (data.turns_used) turnsUsed = data.turns_used as number;
        if (Array.isArray(data.tools_available)) toolsAvailable.push(...(data.tools_available as string[]));
        if (data.message_id) messageId = String(data.message_id);
        if (typeof data.run_status === 'string') runStatus = data.run_status as RunStatus;
        if (Array.isArray(data.artifacts)) artifacts = data.artifacts as ArtifactLink[];
        emitProgress({ status: 'Complete' });
        emitContentUpdate(true);
      },
      onUnknownText: (text) => {
        responseText += text;
      },
    }, { onChunk: resetStreamTimeout });
    if (!receivedDoneEvent && responseText.trim().length === 0) {
      throw new Error('Stream buffer aborted before completion');
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (signal.aborted) throw new Error('Request cancelled by user', { cause: err });
      throw new Error(`Agent request timed out after ${Math.floor(streamTimeoutMs / 1000)}s`, { cause: err });
    }
    throw err;
  } finally {
    if (contentUpdateTimer) {
      clearTimeout(contentUpdateTimer);
      contentUpdateTimer = null;
    }
    cleanupRequest();
  }

  const executionTimeMs = Date.now() - startTime;

  onContentUpdate?.(responseText, true, effectiveThreadId, { runId: runId ?? undefined, messageId });

  return {
    response: responseText,
    tool_calls: toolCalls,
    thinking_stream,
    turns_used: turnsUsed,
    skill_used: agentId,
    datasets_available: [],
    tools_available: toolsAvailable,
    execution_time_ms: executionTimeMs,
    run_id: runId ?? undefined,
    thread_id: threadId ?? undefined,
    request_id: responseRequestId ?? undefined,
    message_id: messageId,
    run_status: runStatus,
    artifacts,
  };
}

/**
 * Attach to an existing agent stream for the given thread by calling
 * GET /api/v1/chat/stream/{threadId}.
 *
 * The backend replays all buffered events emitted so far, then streams live
 * events until the run finishes. This allows a second browser tab (or the same
 * tab after re-selecting an agent via the Back button) to show the exact same
 * streaming output — tool calls, thinking blocks, and text deltas.
 *
 * Returns true if the stream was successfully consumed, false if the backend
 * returned 404 (no active buffer — caller should load the thread from DB).
 */
export async function resubscribeToStream(
  threadId: string,
  onProgress: (progress: StreamProgress) => void,
  onContentUpdate: OnContentUpdate,
  signal?: AbortSignal,
  onThinkingDelta?: (delta: string) => void,
  onThinkingReplace?: (thinking: string) => void,
): Promise<boolean> {
  // No Authorization header — session cookie sent automatically via credentials: 'include'.
  let resp: Response;
  try {
    resp = await fetch(`/api/v1/chat/stream/${encodeURIComponent(threadId)}`, {
      method: 'GET',
      credentials: 'include',
      signal,
    });

    // 401 means the backend session is dead (backend already tried refresh).
    if (resp.status === 401) {
      handleSessionExpired();
      throw new Error('Session expired');
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError' && signal?.aborted) {
      return false;
    }
    const msg = err instanceof Error ? err.message : String(err);
    if (msg === 'Failed to fetch' || (err instanceof TypeError && msg.includes('fetch'))) {
      throw new Error(
        'Failed to connect to the server. Ensure the backend is running on port 8000 (try docker compose up --build).',
        { cause: err },
      );
    }
    throw err;
  }

  if (resp.status === 404) {
    return false;
  }
  if (resp.status === 409) {
    const body = await resp.json().catch(() => ({}));
    throw new ChatConflictError(
      typeof body.detail === 'string' ? body.detail : 'A run is already in progress for this thread',
      body.active_run as ActiveRunInfo | undefined,
    );
  }
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(body.detail ?? `${resp.status} ${resp.statusText}`);
  }

  const reader = resp.body?.getReader();
  if (!reader) throw new Error('No response body');

  let responseText = '';
  let runId: string | undefined;
  let messageId: string | undefined;
  const toolCalls: { tool: string; args: Record<string, unknown>; result?: unknown }[] = [];
  let thinkingStream = '';
  await consumeChatSseStream(reader, {
    onTextDelta: (text) => {
      responseText += text;
      onProgress({ status: 'Generating response...' });
      onContentUpdate(responseText, false, threadId, { runId, messageId });
    },
    onThinking: (thought) => {
      if (thought) {
        thinkingStream = thought;
        onThinkingReplace?.(thought);
        onProgress({ status: 'Thinking...', thinking: thinkingStream });
      } else {
        onProgress({ status: 'Thinking...' });
      }
    },
    onThinkingDelta: (delta) => {
      if (!delta) return;
      thinkingStream += delta;
      onThinkingDelta?.(delta);
      onProgress({ status: 'Thinking...', thinking: thinkingStream });
    },
    onError: (errorMsg) => {
      responseText += `[Agent Error] ${errorMsg}`;
      onProgress({ status: 'Error occurred' });
      onContentUpdate(responseText, true, threadId, { runId, messageId });
    },
    onToolCallStart: (toolName, args) => {
      toolCalls.push({ tool: toolName, args });
      const friendlyName = toolName.replace(/__/g, ' → ').replace(/_/g, ' ');
      onProgress({ status: `Calling tool: ${friendlyName}`, toolName });
    },
    onToolCallEnd: (result) => {
      const lastTc = toolCalls[toolCalls.length - 1];
      if (lastTc) {
        lastTc.result = result;
        const friendlyName = lastTc.tool.replace(/__/g, ' → ').replace(/_/g, ' ');
        onProgress({ status: `Got results from ${friendlyName}`, toolName: lastTc.tool });
      }
    },
    onStatus: (message) => onProgress({ status: message }),
    onDone: (data) => {
      if (data.run_id) runId = String(data.run_id);
      if (data.message_id) messageId = String(data.message_id);
      onProgress({ status: 'Complete' });
      onContentUpdate(responseText, true, threadId, { runId, messageId });
    },
    onUnknownText: (text) => {
      responseText += text;
    },
  });

  return true;
}

// --- Chat History (Server-side threads) ---

export interface ThreadSummary {
  id: string;
  agent_id: string;
  title: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface ThreadMessageToolCall {
  tool: string;
  mcp_id?: string;
  args: Record<string, unknown>;
  result?: unknown;
  turn_number?: number;
}

export interface ThreadMessage {
  id: string;
  role: string;
  content: string;
  run_id?: string | null;
  created_at: string | null;
  /** Debug data for assistant messages (from run) */
  tool_calls?: ThreadMessageToolCall[];
  thinking?: string[];
  thinking_stream?: string;
  turns_used?: number;
  execution_time_ms?: number;
  tools_available?: string[];
  raw_request?: { prompt: string; skill?: string };
  raw_response?: AgentRunResponse;
  run_status?: RunStatus;
  run_error_message?: string | null;
}

export interface ThreadDetail {
  id: string;
  agent_id: string;
  title: string | null;
  messages: ThreadMessage[];
}

export async function fetchChatHistory(limit = 50): Promise<ThreadSummary[]> {
  const resp = await request<{ sessions: ThreadSummary[] }>(`/api/v1/sessions?limit=${limit}`);
  return resp.sessions || [];
}

export async function fetchThreadMessages(
  threadId: string,
  signal?: AbortSignal,
): Promise<ThreadDetail> {
  return request<ThreadDetail>(
    `/api/v1/sessions/${encodeURIComponent(threadId)}`,
    {},
    signal,
  );
}

export async function deleteThread(threadId: string): Promise<void> {
  await request(`/api/v1/sessions/${encodeURIComponent(threadId)}`, { method: 'DELETE' });
}

/** Trigger LLM title generation for a thread. Fire-and-forget — errors are swallowed. */
export async function generateThreadTitle(threadId: string): Promise<string | null> {
  try {
    const resp = await request<{ title: string }>(`/api/v1/sessions/${encodeURIComponent(threadId)}/generate-title`, {
      method: 'POST',
    });
    return resp.title;
  } catch {
    return null;
  }
}

/** Submit feedback for an assistant response. */
export async function submitFeedback(
  runId: string,
  rating: 1 | 0,
  feedbackText: string,
): Promise<{ id: string }> {
  return request<{ id: string }>('/api/v1/feedback', {
    method: 'POST',
    body: JSON.stringify({
      run_id: runId,
      rating,
      feedback_text: feedbackText,
    }),
  });
}

// ── Agent Owner Configuration API ────────────────────────────────

export interface VersionInfo {
  id: string;
  agent_id: string;
  version_number: number;
  is_active: boolean;
  created_by: string;
  source_version_id: string | null;
  change_summary: string | null;
  created_at: string;
}

export interface ConfigVersion extends VersionInfo {
  payload: Record<string, unknown>;
}

export interface BedrockModelOption {
  model_id: string;
  display_name: string;
  provider_name: string;
  region?: string | null;
  lifecycle_status?: string | null;
}

/** The two Claude hosting platforms an agent's model can run on. */
export type ModelProviderId = 'bedrock' | 'azure_foundry';

export interface PromptVersion extends VersionInfo {
  content: string;
  content_scope?: string | null;
  content_source?: string | null;
}

export interface PromptReferenceRuntimeValue {
  key: string;
  value: string | null;
  description: string;
  value_source?: string | null;
  sensitivity?: string | null;
}

export interface PromptReferenceStackItem {
  key: string;
  title: string;
  scope: string;
  authority: string;
  source: string;
  render_location: string;
  editable: boolean;
  content: string;
  sha256: string;
  version?: string | null;
  ttl?: string | null;
  overridable_by?: string | null;
}

export interface PromptReferenceRenderedPrompt {
  adapter: string;
  system_prompt_mode: string;
  setting_sources: string[];
  system_append: string;
  turn_context: string;
  final_preview: string;
}

export interface PromptReference {
  agent_id: string;
  platform_contract: {
    content: string;
    version: string | null;
    sha256: string;
  };
  owner_playbook?: {
    source?: string | null;
    version_number?: number | null;
  };
  runtime_context: {
    rendered: string;
    values: PromptReferenceRuntimeValue[];
  };
  prompt_stack?: PromptReferenceStackItem[];
  default_output_style?: PromptReferenceStackItem | null;
  rendered_prompt?: PromptReferenceRenderedPrompt | null;
}

export interface OwnerInfo {
  id: string;
  agent_id: string;
  user_id: string;
  assigned_by: string;
  is_primary: boolean;
  email: string | null;
  display_name: string | null;
  created_at: string;
}

// ── Owner: Config Versions ──────────────────────────────────────

export async function listConfigVersions(agentId: string): Promise<ConfigVersion[]> {
  const resp = await request<{ versions: ConfigVersion[] }>(`/api/v1/owner/agents/${agentId}/config/versions`);
  return resp.versions;
}

export async function getActiveConfig(agentId: string): Promise<ConfigVersion | null> {
  try {
    return await request<ConfigVersion>(`/api/v1/owner/agents/${agentId}/config/active`);
  } catch {
    return null;
  }
}

export async function listConfigModels(
  agentId: string,
  provider?: ModelProviderId,
): Promise<BedrockModelOption[]> {
  const qs = provider ? `?provider=${encodeURIComponent(provider)}` : '';
  const resp = await request<{ models: BedrockModelOption[] }>(
    `/api/v1/owner/agents/${agentId}/config/models${qs}`,
  );
  return resp.models;
}

export async function publishConfig(
  agentId: string,
  payload: Record<string, unknown>,
  changeSummary?: string,
  opts?: { activate?: boolean },
): Promise<ConfigVersion> {
  const activate = opts?.activate ?? true;
  const qs = activate ? '' : '?activate=false';
  return request<ConfigVersion>(`/api/v1/owner/agents/${agentId}/config/versions${qs}`, {
    method: 'POST',
    body: JSON.stringify({ payload, change_summary: changeSummary ?? null }),
  });
}

/** Save config as an inactive draft for the experiment workflow. */
export async function saveConfigDraft(
  agentId: string,
  payload: Record<string, unknown>,
  changeSummary?: string,
): Promise<ConfigVersion> {
  return publishConfig(agentId, payload, changeSummary, { activate: false });
}

/** Save prompt as an inactive draft for the experiment workflow. */
export async function savePromptDraft(
  agentId: string,
  content: string,
  changeSummary?: string,
): Promise<PromptVersion> {
  return publishPrompt(agentId, content, changeSummary, { activate: false });
}

export async function activateConfigVersion(agentId: string, versionId: string): Promise<ConfigVersion> {
  return request<ConfigVersion>(`/api/v1/owner/agents/${agentId}/config/versions/${versionId}/activate`, {
    method: 'POST',
  });
}

/** Delete an inactive config version (discard an unpublished draft). */
export async function deleteConfigVersion(agentId: string, versionId: string): Promise<void> {
  await request<{ status: string }>(`/api/v1/owner/agents/${agentId}/config/versions/${versionId}`, {
    method: 'DELETE',
  });
}

/** Get the latest inactive (draft) config version, if any. */
export async function getDraftConfig(agentId: string): Promise<ConfigVersion | null> {
  const versions = await listConfigVersions(agentId);
  return versions.find(v => !v.is_active) ?? null;
}

/** Get the latest inactive (draft) prompt version, if any. */
export async function getDraftPrompt(agentId: string): Promise<PromptVersion | null> {
  const versions = await listPromptVersions(agentId);
  return versions.find(v => !v.is_active) ?? null;
}

/** Get the latest inactive (draft) session-context version, if any. */
export async function getDraftSessionContext(agentId: string): Promise<PromptVersion | null> {
  const versions = await listSessionContextVersions(agentId);
  return versions.find(v => !v.is_active) ?? null;
}

// ── Owner: Prompt Versions ──────────────────────────────────────

export async function listPromptVersions(agentId: string): Promise<PromptVersion[]> {
  const resp = await request<{ versions: PromptVersion[] }>(`/api/v1/owner/agents/${agentId}/prompt/versions`);
  return resp.versions;
}

export async function getActivePrompt(agentId: string): Promise<PromptVersion | null> {
  try {
    return await request<PromptVersion>(`/api/v1/owner/agents/${agentId}/prompt/active`);
  } catch {
    return null;
  }
}

export async function getPromptReference(agentId: string): Promise<PromptReference> {
  return request<PromptReference>(
    `/api/v1/owner/agents/${encodeURIComponent(agentId)}/prompt/reference`,
  );
}

export async function publishPrompt(
  agentId: string,
  content: string,
  changeSummary?: string,
  opts?: { activate?: boolean },
): Promise<PromptVersion> {
  const activate = opts?.activate ?? true;
  const qs = activate ? '' : '?activate=false';
  return request<PromptVersion>(`/api/v1/owner/agents/${agentId}/prompt/versions${qs}`, {
    method: 'POST',
    body: JSON.stringify({ content, change_summary: changeSummary ?? null }),
  });
}

export async function activatePromptVersion(agentId: string, versionId: string): Promise<PromptVersion> {
  return request<PromptVersion>(`/api/v1/owner/agents/${agentId}/prompt/versions/${versionId}/activate`, {
    method: 'POST',
  });
}

/** Delete an inactive prompt version (discard an unpublished test draft). */
export async function deletePromptVersion(agentId: string, versionId: string): Promise<void> {
  await request<{ status: string }>(`/api/v1/owner/agents/${agentId}/prompt/versions/${versionId}`, {
    method: 'DELETE',
  });
}

// ── Owner: Session Context Versions ─────────────────────────────

export async function listSessionContextVersions(agentId: string): Promise<PromptVersion[]> {
  const resp = await request<{ versions: PromptVersion[] }>(
    `/api/v1/owner/agents/${agentId}/session-context/versions`,
  );
  return resp.versions;
}

export async function getActiveSessionContext(agentId: string): Promise<PromptVersion | null> {
  try {
    return await request<PromptVersion>(`/api/v1/owner/agents/${agentId}/session-context/active`);
  } catch {
    return null;
  }
}

export async function publishSessionContext(
  agentId: string,
  content: string,
  changeSummary?: string,
  opts?: { activate?: boolean },
): Promise<PromptVersion> {
  const activate = opts?.activate ?? true;
  const qs = activate ? '' : '?activate=false';
  return request<PromptVersion>(`/api/v1/owner/agents/${agentId}/session-context/versions${qs}`, {
    method: 'POST',
    body: JSON.stringify({ content, change_summary: changeSummary ?? null }),
  });
}

/** Save session context as an inactive draft for the experiment workflow. */
export async function saveSessionContextDraft(
  agentId: string,
  content: string,
  changeSummary?: string,
): Promise<PromptVersion> {
  return publishSessionContext(agentId, content, changeSummary, { activate: false });
}

export async function activateSessionContextVersion(agentId: string, versionId: string): Promise<PromptVersion> {
  return request<PromptVersion>(`/api/v1/owner/agents/${agentId}/session-context/versions/${versionId}/activate`, {
    method: 'POST',
  });
}

/** Delete an inactive session-context version. */
export async function deleteSessionContextVersion(agentId: string, versionId: string): Promise<void> {
  await request<{ status: string }>(`/api/v1/owner/agents/${agentId}/session-context/versions/${versionId}`, {
    method: 'DELETE',
  });
}

// ── Owner: Agent Tools & MCPs (read-only) ───────────────────────

export interface AgentTool {
  name: string;
  description: string;
}

export interface AgentMCPTool extends AgentTool {
  /** Tools exposed by this MCP server (empty for external/non-bundled MCPs). */
  tools: AgentTool[];
}

export interface AgentToolsInfo {
  agent_id: string;
  tools: AgentTool[];
  mcps: AgentMCPTool[];
}

export async function getAgentTools(agentId: string): Promise<AgentToolsInfo> {
  return request<AgentToolsInfo>(`/api/v1/owner/agents/${agentId}/capabilities`);
}

// ── Ownership (primary + co-owners) ─────────────────────────────

export interface UserOption {
  user_id: string;
  email: string;
  display_name: string;
}

/** Admin or any owner: search @adobe.com users for owner-management dropdowns.
 *
 * Requires a search query of at least 2 characters — the backend returns an
 * empty list otherwise to avoid dumping the full org user list to any owner.
 * Callers should debounce ~200ms.
 */
export async function listUsers(query: string): Promise<UserOption[]> {
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) return [];
  const qs = new URLSearchParams({ q: trimmed }).toString();
  const resp = await request<{ users: UserOption[] }>(`/api/v1/admin/users?${qs}`);
  return resp.users;
}

export async function listAgentOwners(agentId: string): Promise<OwnerInfo[]> {
  const resp = await request<{ owners: OwnerInfo[] }>(`/api/v1/admin/agents/${agentId}/owners`);
  return resp.owners;
}

/** Admin: set the primary owner for an agent (by @adobe.com email). */
export async function setPrimaryOwner(agentId: string, email: string): Promise<OwnerInfo> {
  return request<OwnerInfo>(`/api/v1/admin/agents/${agentId}/owners/primary`, {
    method: 'PUT',
    body: JSON.stringify({ email }),
  });
}

/** Primary owner or admin: add a co-owner (by @adobe.com email). */
export async function addCoOwner(agentId: string, email: string): Promise<OwnerInfo> {
  return request<OwnerInfo>(`/api/v1/admin/agents/${agentId}/owners`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** Primary owner or admin: remove any owner by internal user id. */
export async function removeAgentOwner(agentId: string, userId: string): Promise<void> {
  await request<{ status: string }>(`/api/v1/admin/agents/${agentId}/owners/${userId}`, {
    method: 'DELETE',
  });
}
